import React from "react";
import styled from "@emotion/styled";
import { Typography } from "../Typography";

type HeaderProps = {
  title?: string;
  subtitle?: string;
  variant: HeaderTypes;
};

export enum HeaderTypes {
  ACCOUNT = "account",
  PROJECT = "project",
  BADGES = "badges",
}

const Container = styled.div`
  overflow: hidden;
  background-color: #fff;
  padding-bottom: 24px;

  @media ${(props) => props.theme.bp.tabletUp} {
    padding-bottom: 16px;
  }
`;

const BackgroundGradientMapper = {
  [HeaderTypes.ACCOUNT]:
    "linear-gradient(180deg, #0cb8e2 0%, #dad8d6 81.04%, #ffffff 100%)",
  [HeaderTypes.PROJECT]:
    "linear-gradient(180deg, #0cb8e2 0%, #dad8d6 81.04%, #ffffff 100%)",
  [HeaderTypes.BADGES]: "linear-gradient(180deg, #0cb8e2 0%, #e9e3dc 86%)",
};

const bottomGapMobile = "40px";
const bottomGapDesktop = "60px";

const GraphicContainer = styled.div<{ variant: string }>`
  display: flex;
  justify-content: center;
  position: relative;
  width: 100%;
  height: calc(376px + ${bottomGapMobile});
  padding-bottom: ${bottomGapMobile};
  background-image: ${({
    variant,
  }) => `url(/images/app/headers/${variant}-foreground-mobile-left.svg),
    url(/images/app/headers/${variant}-foreground-mobile-right.svg),
    url(/images/app/headers/${variant}-background.svg),
    ${BackgroundGradientMapper[variant]}`};
  background-repeat: no-repeat, no-repeat, repeat-x;
  background-position: 0 100%, 100% 100%, 50% calc(100% - ${bottomGapMobile}),
    0 calc(100% - ${bottomGapMobile});

  @media ${(props) => props.theme.bp.tabletUp} {
    height: calc(360px + ${bottomGapDesktop});
    padding-bottom: ${bottomGapDesktop};
    background-image: ${({
      variant,
    }) => `url(/images/app/headers/${variant}-foreground-tablet-left.svg),
      url(/images/app/headers/${variant}-foreground-tablet-right.svg),
      url(/images/app/headers/${variant}-background.svg),
      ${BackgroundGradientMapper[variant]}`};
    background-position: 0 100%, 100% 100%, 50% calc(100% - ${bottomGapDesktop}),
      0 calc(100% - ${bottomGapDesktop});
  }

  @media ${(props) => props.theme.bp.desktopUp} {
    background-image: ${({
      variant,
    }) => `url(/images/app/headers/${variant}-foreground-desktop-left.svg),
      url(/images/app/headers/${variant}-foreground-desktop-right.svg),
      url(/images/app/headers/${variant}-background.svg),
      ${BackgroundGradientMapper[variant]}`};
  }
`;

const DragonflyGraphic = styled.img`
  position: absolute;
  right: 166px;
  top: 102px;
  display: none;

  @media ${(props) => props.theme.bp.desktopUp} {
    display: block;
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  flex-direction: column;
  padding: 24px 48px;
  margin: auto;
  z-index: 1;

  @media ${(props) => props.theme.bp.tabletUp} {
    max-width: 75%;
    padding: 24px;
  }
`;

const Title = styled(Typography)`
  margin-bottom: 8px;

  @media ${(props) => props.theme.bp.tabletUp} {
    margin-bottom: 16px;
  }
`;

export const HeaderBase = ({ title, subtitle, variant }: HeaderProps) => {
  const isProjectInsights = variant === HeaderTypes.PROJECT;

  return (
    <Container>
      <GraphicContainer variant={variant}>
        <HeaderTitle>
          <Title use="pageTitle" as="h1">
            {title}
          </Title>
          <Typography use="subtitle1" as="h2">
            {subtitle}
          </Typography>
        </HeaderTitle>
        {isProjectInsights && (
          <DragonflyGraphic
            src="/images/app/headers/project-foreground-dragonfly.svg"
            alt=""
          />
        )}
      </GraphicContainer>
    </Container>
  );
};
